import { getAccessToken } from '../utils/session';
import {
    BASE_URL,
    CREATE_RULE_SETTING_API,
    DELETE_RULE_API,
    DELETE_CONDITION_API,
    SEARCH_RULES_API,
    DOWNLOAD_RULES_API,
    GET_TDS_SECTION_DROPDOWN_LIST_API,
    GET_RULE_CONDITIONS_API,
    UPDATE_RULE_SETTING_API
} from '../endpoints/apiEndpoints';

export const createRuleSetting = async (ruleData) => {
  try {
    const token = getAccessToken();
    const apiUrl = `${BASE_URL}/${CREATE_RULE_SETTING_API}`;
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      },
      body: JSON.stringify(ruleData),
    });

    const data = await response.json();

    if (!response.ok || data.success === false) {        
      const error =
        data.message ||
        (Array.isArray(data.errors) && data.errors[0]?.message) ||
        (typeof data === 'object' ? Object.values(data).join(', ') : 'Request failed');
      return { success: false, error, data };
    }

    return { success: true, data };
  } catch (error) {    
    return { success: false, error: error.message || 'Failed to create Rule Setting' };
  }
};

export const deleteRuleSetting = async (ruleId) => {
    try {
        const token = getAccessToken();
        const apiUrl = `${BASE_URL}/${DELETE_RULE_API}/${ruleId}`;
        const requestOptions = {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                Authorization: token,
            }
        };
        const response = await fetch(apiUrl, requestOptions);
        if (!response.ok) {
            let errorMessage;
            try {
                const errorData = await response.json();
                errorMessage =
                  errorData?.data ||
                  errorData?.errors[0]?.message ||
                  `Request failed with status: ${response?.status}`
            } catch {
                errorMessage = `Request failed with status: ${response.status}`;
            }
            throw new Error(errorMessage);
        }
        const data = await response.json();
        // If API uses business-level success flag, handle it
        if (data && data.success === false) {
            const errorMessage = data.message 
                || (Array.isArray(data.errors) && data.errors[0] && data.errors[0].message)
                || 'Request failed';
            return {
                success: false,
                error: errorMessage,
                data,
            };
        }
        return {
            success: true,
            data,
        };
    } catch (error) {
        console.error('Failed to create Rule Setting:');
        return {
            success: false,
            error: error?.message || 'Failed to create Rule Setting',
        };
    }
};

export const deleteRuleSettingConditions = async (conditionId) => {
    try {
        const token = getAccessToken();
        const apiUrl = `${BASE_URL}/${DELETE_CONDITION_API}/${conditionId}`;
        const requestOptions = {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                Authorization: token,
            }
        };
        const response = await fetch(apiUrl, requestOptions);
        if (!response.ok) {
            let errorMessage;
            try {
                const errorData = await response.json();
                errorMessage =
                    errorData.message || `Request failed with status: ${response.status}`;
            } catch {
                errorMessage = `Request failed with status: ${response.status}`;
            }
            throw new Error(errorMessage);
        }
        const data = await response.json();
        // If API uses business-level success flag, handle it
        if (data && data.success === false) {
            const errorMessage = data.message 
                || (Array.isArray(data.errors) && data.errors[0] && data.errors[0].message)
                || 'Request failed';
            return {
                success: false,
                error: errorMessage,
                data,
            };
        }
        return {
            success: true,
            data,
        };
    } catch (error) {
        console.error('Failed to create Rule Setting:');
        return {
            success: false,
            error: error.message || 'Failed to create Rule Setting',
        };
    }
};

export const searchRuleSetting = async (ruleData) => {
    try {
        const token = getAccessToken();
        const apiUrl = `${BASE_URL}/${SEARCH_RULES_API}`;
        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: token,
            },
            body: JSON.stringify(ruleData)
        };
        const response = await fetch(apiUrl, requestOptions);
        if (!response.ok) {
            let errorMessage;
            try {
                const errorData = await response.json();
                errorMessage =
                    errorData.message || `Request failed with status: ${response.status}`;
            } catch {
                errorMessage = `Request failed with status: ${response.status}`;
            }
            throw new Error(errorMessage);
        }
        const data = await response.json();
        // If API uses business-level success flag, handle it
        if (data && data.success === false) {
            const errorMessage = data.message 
                || (Array.isArray(data.errors) && data.errors[0] && data.errors[0].message)
                || 'Request failed';
            return {
                success: false,
                error: errorMessage,
                data,
            };
        }
        return {
            success: true,
            data,
        };
    } catch (error) {
        console.error('Failed to search Rule Setting:');
        return {
            success: false,
            error: error.message || 'Failed to search Rule Setting',
        };
    }
};

export const downloadRuleSetting = async ruleData => {
  const token = getAccessToken()
  const apiUrl = `${BASE_URL}/${DOWNLOAD_RULES_API}`
  const requestOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
    },
    body: JSON.stringify(ruleData),
  }
  const res = await fetch(apiUrl, requestOptions)
  if (!res.ok)
    throw new Error(`Failed to download TAN Master Excel: ${res.status}`)

  // Return as Blob (Excel file)
  const blob = await res.blob()
  return blob
};

export const getTdsSectionDropdown = async (tanMasterId) => {
  try {
    const token = getAccessToken()
    const apiUrl = `${BASE_URL}/${GET_TDS_SECTION_DROPDOWN_LIST_API}/${tanMasterId}`;
    const requestOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      }
    }
    const response = await fetch(apiUrl, requestOptions)
    if (!response.ok) {
      let errorMessage
      try {
        const errorData = await response.json()
        errorMessage =
          errorData.message || `Request failed with status: ${response.status}`
      } catch {
        errorMessage = `Request failed with status: ${response.status}`
      }
      throw new Error(errorMessage)
    }
    const data = await response.json()
    return {
      success: true,
      data,
    }
  } catch (error) {
    console.error('Failed to fetch TDS Section dropdown:')
    return {
      success: false,
      error: error.message || 'Failed to fetch TDS Section dropdown',
    }
  }
};

export const getRuleConditions = async (ruleId) => {
    try {
        const token = getAccessToken();
        const apiUrl = `${BASE_URL}/${GET_RULE_CONDITIONS_API}/${ruleId}`;
        const requestOptions = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                Authorization: token,
            }
        };
        const response = await fetch(apiUrl, requestOptions);
        if (!response.ok) {
            let errorMessage;
            try {
                const errorData = await response.json();
                errorMessage =
                    errorData.message || `Request failed with status: ${response.status}`;
            } catch {
                errorMessage = `Request failed with status: ${response.status}`;
            }
            throw new Error(errorMessage);
        }
        const data = await response.json();
        // If API uses business-level success flag, handle it
        if (data && data.success === false) {
            const errorMessage = data.message 
                || (Array.isArray(data.errors) && data.errors[0] && data.errors[0].message)
                || 'Request failed';
            return {
                success: false,
                error: errorMessage,
                data,
            };
        }
        return {
            success: true,
            data,
        };
    } catch (error) {
        console.error('Failed to create Rule Setting:');
        return {
            success: false,
            error: error.message || 'Failed to create Rule Setting',
        };
    }
};

export const updateRuleSetting = async (ruleData) => {
  try {
    const token = getAccessToken();
    const apiUrl = `${BASE_URL}/${UPDATE_RULE_SETTING_API}`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      },
      body: JSON.stringify(ruleData),
    });

    const data = await response.json();

    if (!response.ok || data.success === false) {
      const error =
        data.message ||
        (Array.isArray(data.errors) && data.errors[0]?.message) ||
        (typeof data === 'object' ? Object.values(data).join(', ') : 'Request failed');
      return { success: false, error, data };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Failed to update Rule Setting:');
    return { success: false, error: error.message || 'Failed to update Rule Setting' };
  }
};

